/**
 * What a record on the Sync screen says in plain words: who, what crop, how much, for how much —
 * so the person holding the phone can read out exactly what is (or is not) on the server.
 * Pure: the screen passes the roster lookups in.
 */
import type { OutboxRecord } from '@/offline'

export interface SummaryLookups {
  farmerName: (id: string) => string | null
  cropName: (id: string) => string
}

export interface RecordSummary {
  /** One line under the record's title: "Mohan Lal · मूँगफली · 4 बोरी · 152.4 kg · ₹9,448". */
  line: string | null
  /** Label / value rows for the detail sheet, in the order a person reads them. */
  rows: Array<[label: string, value: string]>
}

type Words = (key: string, vars?: Record<string, string | number>) => string

const kg = (g: number) => `${(g / 1000).toLocaleString('en-IN', { maximumFractionDigits: 2 })} kg`
const rupees = (paise: number) =>
  `₹${(paise / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0)

/** Every photo id a record refers to: the ones listed with it, plus any in its payload. */
export function photoIdsOf(rec: Pick<OutboxRecord, 'media_ids'>, payload: unknown): string[] {
  const out = new Set(rec.media_ids ?? [])
  const walk = (v: unknown, key = '') => {
    if (Array.isArray(v)) v.forEach((x) => walk(x, key))
    else if (v && typeof v === 'object')
      Object.entries(v as Record<string, unknown>).forEach(([k, x]) => walk(x, k))
    else if (typeof v === 'string' && /photo(_media)?_id$/.test(key)) out.add(v)
  }
  walk(payload)
  return [...out]
}

interface Delivery {
  farmer_id?: string
  crop_id?: string
  lots?: Array<{
    code?: string
    stream?: string
    phone_figures?: { gross_g?: number; net_g?: number; payable_g?: number; amount_paise?: number }
  }>
  bags?: Array<{ withdrawn?: boolean }>
  moisture?: { readings?: Array<{ value?: string }>; skipped?: boolean }
  slip?: { slip_no?: string | number | null }
  receipt_slip?: { slip_no?: string | number | null } | null
  reason?: string
}

export function summarise(
  type: string,
  payload: unknown,
  look: SummaryLookups,
  w: Words,
): RecordSummary {
  if (!payload || typeof payload !== 'object') return { line: null, rows: [] }
  const p = payload as Delivery
  const rows: RecordSummary['rows'] = []
  const farmer = p.farmer_id ? (look.farmerName(p.farmer_id) ?? p.farmer_id.slice(0, 8)) : null
  const crop = p.crop_id ? look.cropName(p.crop_id) : null
  if (farmer) rows.push([w('sync.d.farmer'), farmer])
  if (crop) rows.push([w('sync.d.crop'), crop])

  if (type === 'commit_delivery' || type === 'void_delivery') {
    const lots = p.lots ?? []
    const bags = (p.bags ?? []).filter((b) => !b.withdrawn).length
    const figs = lots.map((l) => l.phone_figures ?? {})
    const net = sum(figs.map((f) => f.net_g ?? 0))
    const gross = sum(figs.map((f) => f.gross_g ?? 0))
    const amount = sum(figs.map((f) => f.amount_paise ?? 0))
    for (const l of lots)
      if (l.code) rows.push([w('sync.d.lot'), `${l.code}${l.stream ? ` · ${l.stream}` : ''}`])
    if (bags) rows.push([w('sync.d.bags'), String(bags)])
    if (gross) rows.push([w('sync.d.gross'), kg(gross)])
    if (net) rows.push([w('sync.d.net'), kg(net)])
    if (amount) rows.push([w('sync.d.amount'), rupees(amount)])
    const readings = (p.moisture?.readings ?? []).map((r) => r.value).filter(Boolean)
    if (readings.length) rows.push([w('sync.d.moisture'), readings.map((v) => `${v}%`).join(', ')])
    const slip = p.slip?.slip_no ?? p.receipt_slip?.slip_no
    if (slip != null && slip !== '') rows.push([w('sync.d.slip'), String(slip)])
    if (p.reason) rows.push([w('sync.d.reason'), p.reason])
    const line = [
      farmer,
      crop,
      bags ? w('sync.d.bagsShort', { count: bags }) : null,
      net ? kg(net) : null,
      amount ? rupees(amount) : null,
    ]
      .filter(Boolean)
      .join(' · ')
    return { line: line || null, rows }
  }

  return { line: [farmer, crop].filter(Boolean).join(' · ') || null, rows }
}
